import type { QueryClient } from '@tanstack/react-query';

import type { AppDispatch } from 'state/store';
import {
  ActiveDefectLayer,
  ActiveDefectLayerId,
  buildActiveLayerId,
  layerActivationFailed,
  layerActivationStarted,
  layerActivationSucceeded,
  layerRecipeOwnershipAttached,
  layersRemovedByRecipeId,
} from 'state/surveyDataManager';
import { AvailableViewSummary } from 'domain/surveyDataManager/contracts/availableViews';
import { DefectCatalogItem } from 'domain/surveyDataManager/contracts';
import { expandRecipe } from 'domain/surveyDataManager/recipes/expandRecipe';

import {
  ActivatableMapLayer,
  activateActiveLayers,
} from './pages/HomePage/activateActiveLayers';

export function buildRecipeLayerIds(
  view: AvailableViewSummary,
  catalogDefects: readonly DefectCatalogItem[],
): ActiveDefectLayerId[] {
  const expansion = expandRecipe(
    view,
    catalogDefects,
    new Set(),
    buildActiveLayerId,
  );
  return expansion.layers.map((layer) =>
    buildActiveLayerId({
      defectSlug: layer.defectSlug,
      measurementSlug: layer.measurementSlug,
      surveyYearSlug: layer.surveyYearSlug,
      rulesetSlug: layer.rulesetSlug,
      lane: layer.lane,
    }),
  );
}

/**
 * Recipe ids that should show as "See in Map" / favourite-checked.
 * Includes owned layers and orphan layers whose ids match a recipe expansion.
 */
export function collectActiveRecipeIds(
  layers: readonly ActiveDefectLayer[],
  views: readonly AvailableViewSummary[],
  catalogDefects: readonly DefectCatalogItem[],
): Set<string> {
  const activeIds = new Set(
    layers.filter((layer) => layer.visible).map((layer) => layer.id),
  );
  const recipeIds = new Set<string>();

  for (const layer of layers) {
    if (layer.recipeId && layer.visible) {
      recipeIds.add(layer.recipeId);
    }
  }

  if (catalogDefects.length === 0) {
    return recipeIds;
  }

  for (const view of views) {
    if (recipeIds.has(view.id)) {
      continue;
    }
    const matchingIds = buildRecipeLayerIds(view, catalogDefects);
    if (matchingIds.some((id) => activeIds.has(id))) {
      recipeIds.add(view.id);
    }
  }

  return recipeIds;
}

/** Attach recipe ownership to orphan layers that already match a view expansion. */
export function claimMatchingRecipeOwnership(
  dispatch: AppDispatch,
  layers: readonly ActiveDefectLayer[],
  views: readonly AvailableViewSummary[],
  catalogDefects: readonly DefectCatalogItem[],
): void {
  if (catalogDefects.length === 0 || views.length === 0) {
    return;
  }

  const byId = new Map(layers.map((layer) => [layer.id, layer]));

  for (const view of views) {
    for (const layerId of buildRecipeLayerIds(view, catalogDefects)) {
      const existing = byId.get(layerId);
      if (!existing || existing.recipeId != null) {
        continue;
      }
      // Only claim orphans (recipeId null). Do not force-show owned but hidden layers.
      if (!existing.visible) {
        continue;
      }
      dispatch(
        layerRecipeOwnershipAttached({
          id: layerId,
          recipeId: view.id,
        }),
      );
    }
  }
}

export async function activateRecipeView(options: {
  readonly view: AvailableViewSummary;
  readonly catalogDefects: readonly DefectCatalogItem[];
  readonly activeLayers: readonly ActiveDefectLayer[];
  readonly dispatch: AppDispatch;
  readonly queryClient: QueryClient;
}): Promise<void> {
  const { view, catalogDefects, activeLayers, dispatch, queryClient } = options;
  const activeById = new Map(activeLayers.map((layer) => [layer.id, layer]));
  const expansion = expandRecipe(
    view,
    catalogDefects,
    new Set(),
    buildActiveLayerId,
  );

  dispatch(layerActivationStarted({ kind: 'recipe', recipeId: view.id }));
  try {
    if (expansion.layers.length === 0) {
      dispatch(
        layerActivationFailed({
          message: `Could not activate ${view.name}: no layers to add.`,
        }),
      );
      return;
    }

    const layersToAdd: ActivatableMapLayer[] = [];
    for (const layer of expansion.layers) {
      const layerId = buildActiveLayerId(layer);
      const existing = activeById.get(layerId);
      if (existing) {
        if (existing.recipeId == null || existing.recipeId === view.id) {
          dispatch(
            layerRecipeOwnershipAttached({
              id: layerId,
              recipeId: view.id,
            }),
          );
        }
        continue;
      }
      layersToAdd.push(layer);
    }

    if (layersToAdd.length > 0) {
      await activateActiveLayers({
        layers: layersToAdd,
        dispatch,
        queryClient,
        existingLayerIds: new Set(activeById.keys()),
      });
    }

    dispatch(layerActivationSucceeded());
  } catch (error) {
    dispatch(
      layerActivationFailed({
        message:
          error instanceof Error
            ? error.message
            : `Could not activate ${view.name}.`,
      }),
    );
    throw error;
  }
}

export function deactivateRecipeView(options: {
  readonly view: AvailableViewSummary;
  readonly catalogDefects: readonly DefectCatalogItem[];
  readonly dispatch: AppDispatch;
}): void {
  const matchingLayerIds = buildRecipeLayerIds(
    options.view,
    options.catalogDefects,
  );
  options.dispatch(
    layersRemovedByRecipeId({
      recipeId: options.view.id,
      matchingLayerIds,
    }),
  );
}
